import React, { useState } from "react";
import {
  Box,
  FormHelperText,
  IconButton,
  Button,
  Checkbox,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FieldArray, useFormikContext, Field } from "formik";
import QuestionTextField from "./QuestionTextField";
import AnswersField from "./AnswersField";
import { PropTypes } from "prop-types";
import HTMLEditor from "../../HtmlEditor/HtmlEditor";
import { Delete as DeleteIcon, Add } from "@mui/icons-material";
import { v4 as uuid } from "uuid";
import { CheckboxWithLabel } from "formik-mui";
import DraggableList from "../../DraggableList/DraggableList";

const htmlEditorToolbar =
  "undo redo | bold italic underline | alignment numlist bullist outdent indent | link image media fileUpload";

const getNewMultiSelectAnswer = order => ({
  order,
  text: "",
  id: uuid(),
  type: "answer"
});

const MultiSelectField = ({
  answer,
  answerIndex,
  onDelete,
  onEdit,
  showCheckbox,
  dragHandle,
  isDragging,
  questionFilesList,
  setQuestionFilesList
}) => {
  const { t } = useTranslation();

  const [isHovering, setIsHovering] = useState(false);

  return answer ? (
    <Box
      display="flex"
      alignItems="center"
      width={{ xs: "unset", md: "60%" }}
      justifyContent="space-between"
      mb={2}
      pl={3}
      position="relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {(isHovering || isDragging) && (
        <Box position="absolute" ml={-4} padding={1}>
          {dragHandle}
        </Box>
      )}
      {showCheckbox && (
        <Checkbox
          checked={answer.correct}
          color="primary"
          onChange={({ target: { checked } }) =>
            onEdit({ ...answer, correct: checked })
          }
        />
      )}
      <Box flex={1} minHeight="100%" maxWidth="100%">
        <HTMLEditor
          disableMargin
          onChange={text => onEdit({ ...answer, text })}
          value={answer.text}
          menubar={false}
          plugins={["lists", "link", "image", "media", "code"]}
          toolbar={htmlEditorToolbar}
          onFileAdd={(fileObj, tempLink, type) => {
            const list = questionFilesList;
            list.push({ fileObj, tempLink, type });
            setQuestionFilesList(list);
          }}
          inline={true}
          toolbarId={`toolbar-${answer.id}`}
          disableResize
          placeholder={t("editQuestion.multipleChoiceAnswerPlaceholder")}
        />
      </Box>
      <Box display="flex" alignItems="center" ml={1}>
        <IconButton onClick={() => onDelete(answerIndex)} size="large">
          <DeleteIcon color="primary" />
        </IconButton>
      </Box>
    </Box>
  ) : null;
};

const MultiSelectFields = ({
  isSurveyQuestion,
  questionFilesList,
  setQuestionFilesList,
  onReorderAnswers,
  onDelete
}) => {
  const { t } = useTranslation();
  const { errors, values, submitCount } = useFormikContext();

  return (
    <Box display="flex" flexDirection="column">
      <QuestionTextField
        questionFilesList={questionFilesList}
        setQuestionFilesList={setQuestionFilesList}
      />
      <AnswersField
        renderAnswers={() => (
          <Box display="flex" flexDirection="column" flex={1}>
            {!isSurveyQuestion && (
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 4.5 }}>
                {t("editQuestion.multiSelectQuestionQuizNote")}
              </Typography>
            )}
            <FieldArray
              name="answers"
              render={({ push, replace }) => (
                <Box display="flex" flexDirection="column" flex={1} ml={-3}>
                  <DraggableList
                    onDragEnd={onReorderAnswers}
                    droppableId="multiple-select-responses"
                    items={values.answers.map((answer, index) => ({
                      draggableId: answer.id,
                      render: ({ dragHandle, isDragging }) => (
                        <MultiSelectField
                          key={answer.id}
                          answerIndex={index}
                          onDelete={onDelete}
                          answer={answer}
                          onEdit={newAnswer => replace(index, newAnswer)}
                          showCheckbox={!isSurveyQuestion}
                          dragHandle={dragHandle}
                          isDragging={isDragging}
                          questionFilesList={questionFilesList}
                          setQuestionFilesList={setQuestionFilesList}
                        />
                      )
                    }))}
                  />
                  {!!errors.answers && submitCount > 0 && (
                    <Box ml={isSurveyQuestion ? 0 : 6} mb={2}>
                      <FormHelperText error={true}>
                        {errors.answers}
                      </FormHelperText>
                    </Box>
                  )}
                  <Box mb={2} mt={1.5} ml={3}>
                    <Button
                      color="primary"
                      startIcon={<Add />}
                      onClick={() =>
                        push(getNewMultiSelectAnswer(values.answers.length))
                      }
                      data-qa="add-short-answer-btn"
                    >
                      {t("editQuestion.addAnswerBtn")}
                    </Button>
                  </Box>
                </Box>
              )}
            />
          </Box>
        )}
      />
      <Box mt={3.5} mb={2}>
        <Field
          component={CheckboxWithLabel}
          name="randomizeAnswers"
          inputProps={{
            "data-qa": "randomize-answers-checkbox"
          }}
          Label={{
            label: (
              <Typography variant="subtitle1">
                {t("editQuestion.randomizeAnswersLabel")}
              </Typography>
            )
          }}
          color="primary"
          type="checkbox"
        />
      </Box>
    </Box>
  );
};

MultiSelectFields.propTypes = {
  isSurveyQuestion: PropTypes.bool,
  questionFilesList: PropTypes.array,
  setQuestionFilesList: PropTypes.func,
  onReorderAnswers: PropTypes.func,
  onDelete: PropTypes.func
};

export default MultiSelectFields;
